import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchData } from "../redux/apiSlice";
import { toastify, userlogout } from "../redux/controller";
import "react-toastify/dist/ReactToastify.css";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import VisibilityIcon from "@mui/icons-material/Visibility";

export default function Affiliates() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedSeason = useSelector((state) => state.season.selectedSeason);
  const usertoken = localStorage.getItem("usertoken");

  const [affiliateData, setAffiliateData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("Loading...");
  const [searchInput, setSearchInput] = useState("");
  const [expandedRows, setExpandedRows] = useState({});

  const fetchAffiliates = async () => {
    setLoading(true);
    setStatus("Loading...");
    try {
      const response = await dispatch(
        fetchData({
          apiurl: "/auth/user/landownerswithsubs",
          seasonEndDate: selectedSeason?.seasonEndDate,
          seasonStartDate: selectedSeason?.seasonStartDate,
          headers: { "x-access-token": usertoken },
        })
      );

      if (response && response.payload && response.payload.status === 200) {
        setAffiliateData(response.payload.data || []);
        if ((response.payload.data || []).length === 0) {
          setStatus("No affiliates found!");
        }
      } else {
        setStatus("Failed to load affiliates");
        if (
          response?.payload?.response?.data?.message === "Invalid token provided!" ||
          response?.payload?.response?.data?.message === "No token provided!"
        ) {
          userlogout();
          navigate("/");
        }
      }
    } catch (error) {
      console.error("Error fetching affiliates:", error);
      setStatus("Error loading data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAffiliates();
  }, [selectedSeason]);

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleViewDetails = (user) => {
    if (!user) return;
    navigate("/landownersdetails", {
      state: {
        id: user._id,
        Landowner: `${user.firstname || ""} ${user.lastname || ""}`.trim(),
        email: user.email || "--",
        phone_number: user.phonenumber || "--",
        createdAt: user.createdAt || new Date().toISOString(),
        affiliateTotalEarnings: user.affiliateTotalEarnings ?? 0,
        affiliateCommissionPercentage: user.affiliateCommissionPercentage ?? 5,
      },
    });
  };

  // Filter main landowners or their sub-landowners by name or email
  const filteredData = affiliateData.filter((item) => {
    const mainName = `${item.landowner?.firstname || ""} ${item.landowner?.lastname || ""}`.toLowerCase();
    const mainEmail = (item.landowner?.email || "").toLowerCase();
    const matchesMain = mainName.includes(searchInput.toLowerCase()) || mainEmail.includes(searchInput.toLowerCase());

    const matchesSubs = item.subLandowners?.some((sub) => {
      const subName = `${sub.firstname || ""} ${sub.lastname || ""}`.toLowerCase();
      const subEmail = (sub.email || "").toLowerCase();
      return subName.includes(searchInput.toLowerCase()) || subEmail.includes(searchInput.toLowerCase());
    });

    return matchesMain || matchesSubs;
  });

  // Calculate high-level stats
  const totalParents = affiliateData.length;
  const totalSubs = affiliateData.reduce((sum, item) => sum + (item.subLandowners?.length || 0), 0);
  
  const addedLandownerIds = new Set();
  const totalEarnings = affiliateData.reduce((sum, item) => {
    let currentSum = 0;

    if (item.landowner && item.landowner._id) {
      const parentId = String(item.landowner._id);
      if (!addedLandownerIds.has(parentId)) {
        addedLandownerIds.add(parentId);
        currentSum += Number(item.landowner.affiliateTotalEarnings) || 0;
      }
    }

    if (Array.isArray(item.subLandowners)) {
      item.subLandowners.forEach((sub) => {
        if (sub && sub._id) {
          const subId = String(sub._id);
          if (!addedLandownerIds.has(subId)) {
            addedLandownerIds.add(subId);
            currentSum += Number(sub.affiliateTotalEarnings) || 0;
          }
        }
      });
    }

    return sum + currentSum;
  }, 0);

  return (
    <>
      {/* Custom Styles for Modern Glassmorphism & Visual Accents */}
      <style>{`
        .affiliate-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 20px;
          margin-bottom: 25px;
        }
        .stat-card {
          background: #fff;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
          border-left: 5px solid #be7637;
          transition: transform 0.2s ease-in-out;
        }
        .stat-card:hover {
          transform: translateY(-3px);
        }
        .stat-title {
          font-size: 14px;
          color: #888;
          margin-bottom: 8px;
          text-transform: uppercase;
          font-weight: 600;
          letter-spacing: 0.5px;
        }
        .stat-value {
          font-size: 28px;
          font-weight: 700;
          color: #202620;
        }
        .affiliates-table-container {
          background: #fff;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
          overflow: hidden;
          border: 1px solid #bd753e30;
        }
        .affiliates-table {
          width: 100%;
          border-collapse: collapse;
        }
        .affiliates-table th {
          background: #fbf6f1;
          color: #be7637;
          font-weight: 600;
          text-align: left;
          padding: 14px 16px;
          font-size: 14px;
          border-bottom: 2px solid #f0e6dd;
        }
        .affiliates-table td {
          padding: 14px 16px;
          font-size: 14px;
          border-bottom: 1px solid #f2f2f2;
          color: #5a5a5a;
        }
        .parent-row {
          background: #fff;
          transition: background 0.15s ease-in-out;
        }
        .parent-row:hover {
          background: #fcfaf8;
        }
        .sub-landowners-row {
          background: #fafaf9;
        }
        .sub-table-wrapper {
          padding: 15px 25px 20px 45px;
          border-left: 3px solid #be7637;
          background: #fafaf9;
        }
        .sub-table {
          width: 100%;
          background: #fff;
          border-radius: 8px;
          border: 1px solid #e9e9e9;
          border-collapse: collapse;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.02);
        }
        .sub-table th {
          background: #fdfdfd;
          color: #be7637;
          font-size: 13px;
          padding: 10px 12px;
          border-bottom: 1px solid #e9e9e9;
        }
        .sub-table td {
          padding: 10px 12px;
          font-size: 13px;
          border-bottom: 1px solid #f4f4f4;
        }
        .sub-table tr:hover {
          background: #f9f9f9;
        }
        .view-btn {
          background: transparent;
          border: 1px solid #be7637;
          color: #be7637;
          border-radius: 6px;
          padding: 5px 12px;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .view-btn:hover {
          background: #be7637;
          color: #fff;
        }
        .toggle-btn {
          background: none;
          border: none;
          color: #be7637;
          cursor: pointer;
          padding: 0;
          display: flex;
          align-items: center;
        }
        .badge-subs {
          background-color: #be7637;
          color: white;
          border-radius: 12px;
          padding: 2px 8px;
          font-size: 11px;
          font-weight: 600;
          margin-left: 8px;
        }
        .badge-main {
          background-color: #e5f6df;
          color: #2b701c;
          border-radius: 12px;
          padding: 2px 8px;
          font-size: 11px;
          font-weight: 600;
        }
        .badge-sub-role {
          background-color: #e0f0ff;
          color: #125bb2;
          border-radius: 12px;
          padding: 2px 8px;
          font-size: 11px;
          font-weight: 600;
        }
      `}</style>

      <div className="landowner-main">
        {/* Stats Row */}
        <div className="affiliate-stats">
          <div className="stat-card">
            <div className="stat-title">Main Landowners</div>
            <div className="stat-value">{totalParents}</div>
          </div>
          <div className="stat-card">
            <div className="stat-title">Sub-Landowners</div>
            <div className="stat-value">{totalSubs}</div>
          </div>
          <div className="stat-card">
            <div className="stat-title">Total Affiliates Earnings</div>
            <div className="stat-value">${totalEarnings.toLocaleString()}</div>
          </div>
        </div>

        {/* Search Header */}
        <div className="search-div">
          <div className="input-wrapper">
            <input
              type="text"
              className="form-control hunter-search"
              placeholder="Search by Landowner Name or Email..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <button className="clear-btn" onClick={() => setSearchInput("")}>
                ×
              </button>
            )}
          </div>
        </div>

        {/* Accordion Table */}
        <div className="affiliates-table-container">
          <table className="affiliates-table">
            <thead>
              <tr>
                <th style={{ width: "60px" }}></th>
                <th>Landowner Name</th>
                <th>Email Address</th>
                <th>Commission (%)</th>
                <th>Total Affiliates Earnings</th>
                <th style={{ width: "150px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center", padding: "40px" }}>
                    {status}
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center", padding: "40px" }}>
                    No results match your search query.
                  </td>
                </tr>
              ) : (
                filteredData.map((item) => {
                  const id = item.landowner?._id;
                  const name = `${item.landowner?.firstname || ""} ${item.landowner?.lastname || ""}`.trim() || "--";
                  const email = item.landowner?.email || "--";
                  const commission = item.landowner?.affiliateCommissionPercentage ?? 0;
                  const earnings = item.landowner?.affiliateTotalEarnings ?? 0;
                  const hasSubs = item.subLandowners && item.subLandowners.length > 0;
                  const isExpanded = !!expandedRows[id];

                  return (
                    <React.Fragment key={id}>
                      {/* Main Landowner Row */}
                      <tr className="parent-row">
                        <td>
                          {hasSubs ? (
                            <button className="toggle-btn" onClick={() => toggleRow(id)}>
                              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </button>
                          ) : null}
                        </td>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <strong>{name}</strong>
                            <span className="badge-main">Main</span>
                            {hasSubs && (
                              <span className="badge-subs">
                                {item.subLandowners.length} sub-landowner{item.subLandowners.length > 1 ? "s" : ""}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>{email}</td>
                        <td>{commission}%</td>
                        <td>${earnings.toLocaleString()}</td>
                        <td>
                          <button className="view-btn" onClick={() => handleViewDetails(item.landowner)}>
                            <VisibilityIcon style={{ fontSize: "16px" }} />
                            View
                          </button>
                        </td>
                      </tr>

                      {/* Sub-Landowners Collapsible Section */}
                      {hasSubs && isExpanded && (
                        <tr className="sub-landowners-row">
                          <td colSpan="6" style={{ padding: 0 }}>
                            <div className="sub-table-wrapper">
                              <h5
                                style={{
                                  fontSize: "13px",
                                  fontWeight: "600",
                                  color: "#be7637",
                                  marginBottom: "10px",
                                }}
                              >
                                Sub-Landowners of {name}
                              </h5>
                              <table className="sub-table">
                                <thead>
                                  <tr>
                                    <th>Name</th>
                                    <th>Email Address</th>
                                    <th>Commission (%)</th>
                                    <th>Total Affiliates Earnings</th>
                                    <th style={{ width: "130px" }}>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {item.subLandowners.map((sub) => {
                                    const subName = `${sub.firstname || ""} ${sub.lastname || ""}`.trim() || "--";
                                    return (
                                      <tr key={sub._id}>
                                        <td>
                                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            <strong>{subName}</strong>
                                            <span className="badge-sub-role">Sub</span>
                                          </div>
                                        </td>
                                        <td>{sub.email || "--"}</td>
                                        <td>{sub.affiliateCommissionPercentage ?? 0}%</td>
                                        <td>${(sub.affiliateTotalEarnings ?? 0).toLocaleString()}</td>
                                        <td>
                                          <button className="view-btn" onClick={() => handleViewDetails(sub)}>
                                            <VisibilityIcon style={{ fontSize: "14px" }} />
                                            View
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
